import { CovLoading } from "@cx-component-library/react-components";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { BreadcrumbsPath } from "components/commons/BreadscrumbsPath";
import { useAuthUser } from "hooks/authentication";
import { useHelpCenter } from "hooks/help-center";
import { type HelpSearchData, useHelpSearch } from "hooks/help-search";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import React, { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { authenNavigate } from "service/utils/authen-navigate";
import { checkInternalUser } from "service/utils/get-user-type";
import { emptyItem, type Item } from "./HelpConstant";
import HelpPageContent from "./HelpPageContent";
import "./HelpPage.css";
import { useTranslation } from "react-i18next";

export const HelpPage = () => {
  authenNavigate();
  const [searchText, setSearchText] = useState<string>("");
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const { user } = useAuthUser();
  const isInternal: boolean = checkInternalUser(user, settings);
  const [targetItem, setTargetItem] = useState<Item>();
  const [searchData, setSearchData] = useState<HelpSearchData[] | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const [helpCenterApiData, , isLoading] = useHelpCenter(
    settings.language.toLowerCase(),
    isInternal,
  );

  const { search, isLoading: isSearchLoading } = useHelpSearch(isInternal);

  const handleSearchContent = async () => {
    if (searchText) {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("searchText", searchText.trim());
        return newParams;
      });
      const results = await search(
        searchText.toLowerCase(),
        settings.language.toLowerCase(),
      );
      setSearchData(results || []);
    } else {
      setSearchData(undefined);
    }
  };

  const handleChangeSearchText = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchText(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearchContent();
    }
  };

  const category = React.useMemo(() => {
    return searchParams.get("category");
  }, [searchParams]);

  const section = React.useMemo(() => {
    return searchParams.get("section");
  }, [searchParams]);

  const searchTextParams = React.useMemo(() => {
    return searchParams.get("searchText");
  }, [searchParams]);

  React.useEffect(() => {
    if (helpCenterApiData?.data.length) {
      const { data: helpCenterData } = helpCenterApiData;
      if (section && category) {
        const sectionData = helpCenterData.find((i) => i.id === section);

        if (sectionData) {
          const categoryData = sectionData.items.find(
            (i: any) => i.id === category,
          );

          if (categoryData) {
            return setTargetItem(categoryData);
          }
        }
      } else {
        return setTargetItem(helpCenterData[0].items[0]);
      }
    }
    return setTargetItem(emptyItem);
  }, [section, category, helpCenterApiData]);

  React.useEffect(() => {
    if (searchTextParams) {
      setSearchText(searchTextParams);
      const executeSearch = async () => {
        const results = await search(
          searchTextParams.toLowerCase(),
          settings.language.toLowerCase(),
        );
        setSearchData(results || []);
      };
      executeSearch();
    }
  }, [searchTextParams, settings.language, search]);

  const goBackHelpPage = (): void => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("searchText");
      return newParams;
    });
    setSearchText("");
    setSearchData(undefined);
    setSelectedCategory("all");
  };

  const handleCategoryChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    setSelectedCategory(newValue);
  };

  const handleReadMore = (item: HelpSearchData) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("category", item.id);
      newParams.set("section", item.categoryId);
      newParams.delete("searchText");
      return newParams;
    });
    setSearchText("");
    setSearchData(undefined);
  };

  const categories = React.useMemo(() => {
    if (!searchData) return [];

    const categoryMap = new Map<string, number>();
    searchData.forEach((item) => {
      const category = item.categoryName || "Other";
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    return Array.from(categoryMap.entries()).map(([name, count]) => ({
      name,
      count,
    }));
  }, [searchData]);

  const filteredSearchData = React.useMemo(() => {
    if (!searchData) return undefined;
    if (selectedCategory === "all") return searchData;
    return searchData.filter((item) => item.categoryName === selectedCategory);
  }, [searchData, selectedCategory]);

  return (
    <Grid
      container
      mt={0}
      mb={0}
      rowSpacing={3}
      columnSpacing={4}
      className="help-page-container"
    >
      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
        <BreadcrumbsPath
          pathName="Help Center"
          onClick={goBackHelpPage}
          showSearchResults={!!searchTextParams}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
        <Box className="help-page-search-container">
          <Typography className="help-page-title" variant="h6" component="div">
            How can we help you?
          </Typography>
          <FormControl className="help-page-search-form" variant="outlined">
            <TextField
              id="outlined-adornment-password"
              type="search"
              value={searchText}
              onChange={handleChangeSearchText}
              onKeyDown={handleKeyDown}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className="help-page-search-button"
                        aria-label={"Search"}
                        onClick={handleSearchContent}
                        disabled={!searchText}
                        edge="end"
                      >
                        <SearchIcon className="help-page-search-button" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            {searchData && searchData.length > 0 && (
              <Box className="help-results-info">
                <Typography className="help-results-count">
                  Results <b>1-{Math.min(5, searchData.length)}</b> of{" "}
                  <b>{searchData.length}</b>
                </Typography>
              </Box>
            )}
          </FormControl>
        </Box>
      </Grid>

      {searchData && categories.length > 0 && (
        <Grid size={{ xs: 12, md: 12, xl: 12 }}>
          <Box className="help-category-bar">
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              className="help-category-tabs"
            >
              <Tab
                label={`All (${searchData.length})`}
                value="all"
                className="help-category-tab"
              />
              {categories.map((category) => (
                <Tab
                  key={category.name}
                  label={`${category.name} (${category.count})`}
                  value={category.name}
                  className="help-category-tab"
                />
              ))}
            </Tabs>
          </Box>
        </Grid>
      )}
      {isLoading || isSearchLoading ? (
        <CovLoading />
      ) : (
        <HelpPageContent
          helpCenterApiData={helpCenterApiData}
          targetItem={targetItem}
          setSearchParams={setSearchParams}
          searchData={filteredSearchData}
          handleReadMore={handleReadMore}
          t={t}
        />
      )}
    </Grid>
  );
};
